import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowDown,
  Check,
  X,
  MessageSquare,
  Handshake,
  Sparkles,
  Users,
  Building2,
  Home,
  UserCheck,
  Megaphone,
  Wrench,
  CalendarClock,
  Phone,
  TrendingUp,
  Award,
  Loader2,
  Send,
  CheckCircle2,
  Download,
  MessageCircle,
  ScrollText,
  ImageIcon,
  Mail,
  BadgeDollarSign,
  ShieldCheck,
  PlayCircle,
  Video,
  Compass,
  Trophy,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ASSETS, CONTACT } from "@/lib/constants";
import { track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* --- helpers -------------------------------------------------- */
function useNoIndex(title, description) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    const upsertMeta = (attr, name, content) => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      return { el, prev };
    };

    const robots = upsertMeta("name", "robots", "noindex,nofollow,noarchive,nosnippet");
    const googlebot = upsertMeta("name", "googlebot", "noindex,nofollow");
    const desc = upsertMeta("name", "description", description);

    return () => {
      document.title = prevTitle;
      robots.el.setAttribute("content", robots.prev || "");
      googlebot.el.setAttribute("content", googlebot.prev || "");
      desc.el.setAttribute("content", desc.prev || "");
    };
  }, [title, description]);
}

const ApplyCTA = ({ testid = "bdm-apply-cta", label = "Apply to become a Partner", variant = "primary" }) => {
  const cls = {
    primary:
      "inline-flex items-center gap-2 bg-white hover:bg-white/95 text-navy-deep font-semibold text-base px-8 py-4 rounded-full shadow-cta hover:shadow-cta-hover transition-all hover:-translate-y-0.5 ring-2 ring-lime/70",
    secondary:
      "inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white font-semibold text-base px-8 py-4 rounded-full shadow-cta hover:shadow-cta-hover transition-all hover:-translate-y-0.5",
    lime:
      "inline-flex items-center gap-2 bg-lime hover:bg-lime-hover text-navy-deep font-semibold text-base px-8 py-4 rounded-full shadow-cta hover:shadow-cta-hover transition-all hover:-translate-y-0.5",
  }[variant] || "";
  return (
    <a data-testid={testid} href="/partnerapply" className={cls}>
      {label}
      <ArrowRight size={18} />
    </a>
  );
};

const Eyebrow = ({ children }) => (
  <p className="text-xs font-bold tracking-[0.22em] uppercase text-teal">{children}</p>
);

const SectionHeading = ({ eyebrow, title, sub, testid }) => (
  <div className="max-w-3xl" data-testid={testid}>
    <Eyebrow>{eyebrow}</Eyebrow>
    <h2 className="mt-4 font-heading font-medium text-3xl sm:text-4xl lg:text-5xl text-balance">
      {title}
    </h2>
    {sub && <p className="mt-5 text-lg text-ink-600 leading-relaxed">{sub}</p>}
  </div>
);

/* Full-width band that marks the start of an audience track */
const TrackHeader = ({ id, number, label, title, sub, testid }) => (
  <div id={id} data-testid={testid} className="scroll-mt-20 bg-navy-deep text-white">
    <div className="container-grc py-12 sm:py-16">
      <p className="inline-flex items-center gap-3 text-xs font-bold tracking-[0.22em] uppercase text-lime">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-lime text-navy-deep text-sm tabular-nums">{number}</span>
        {label}
      </p>
      <h2 className="mt-5 font-heading font-medium text-3xl sm:text-4xl lg:text-5xl text-balance max-w-3xl">{title}</h2>
      {sub && <p className="mt-4 text-lg text-white/70 leading-relaxed max-w-2xl">{sub}</p>}
    </div>
  </div>
);

/* Track chooser data for the hero */
const TRACKS = [
  {
    id: "bdm",
    icon: <Handshake size={22} />,
    who: "I'm a real estate agent or connector",
    line: "Turn the conversations you're already having into a second revenue stream — no license required.",
    cta: "See the Partner track",
  },
  {
    id: "brokerage",
    icon: <Building2 size={22} />,
    who: "I lead a team or brokerage",
    line: "Recruit, retain, and reward your agents — with brokerage-level revenue opportunities on top.",
    cta: "See the Brokerage track",
  },
];

/* --- data ----------------------------------------------------- */
const AUDIENCE = [
  { icon: <Home size={18} />, label: "Real estate agents" },
  { icon: <Users size={18} />, label: "Real estate teams" },
  { icon: <Building2 size={18} />, label: "Brokerage leaders" },
  { icon: <Handshake size={18} />, label: "Business development professionals" },
  { icon: <Sparkles size={18} />, label: "Financial professionals" },
  { icon: <MessageSquare size={18} />, label: "Community connectors" },
  { icon: <Megaphone size={18} />, label: "Content creators" },
  { icon: <UserCheck size={18} />, label: "Licensed MLOs" },
  { icon: <Users size={18} />, label: "Professionals with a database or local audience" },
];

const YOU_DO_NOT = [
  "Become a licensed loan officer",
  "Quote rates or loan terms",
  "Structure loan files",
  "Collect loan documents",
  "Process, underwrite, or close loans",
  "Give mortgage advice",
  "Take over the loan conversation",
];

const YOU_CAN = [
  "Share approved marketing messages",
  "Introduce people to the Great Rate Check process",
  "Create conversations with your network",
  "Help consumers connect with licensed mortgage professionals",
  "Earn on every closed loan you help introduce",
  "Support approved business development activity",
  "Build a new income and relationship channel",
];

const OUTREACH_TOPICS = [
  "Purchase loans",
  "Refinance opportunities",
  "HELOC conversations",
  "Rate reviews",
  "Past client follow-up",
  "Agent database outreach",
  "Social media messaging",
  "Text and email campaigns",
  "Community-based marketing",
];

const AGENT_USES = [
  "Stay in front of past clients",
  "Create refinance and HELOC conversations",
  "Support buyers before they commit to a rate",
  "Add value without giving mortgage advice",
  "Strengthen your lending relationship",
  "Build a repeatable outreach campaign",
];

const MLO_SUPPORT = [
  "Simple consumer-facing messaging",
  "Monthly marketing themes",
  "Social content ideas",
  "Referral partner outreach tools",
  "Partner collaboration opportunities",
  "Database reactivation campaigns",
  "Brand consistency",
  "A stronger reason to follow up",
];

const PARTNER_SUPPORT = [
  "Monthly content ideas",
  "Approved messaging themes",
  "Social post concepts",
  "Text and email scripts",
  "Database outreach prompts",
  "Partner meeting updates",
  "Strategy calls",
  "Compliance-conscious marketing guidance",
  "Campaign ideas for agents and teams",
];

const STEPS = [
  {
    n: "01",
    title: "Apply through Great Rate Check",
    body: "A short application. We review your profile and let you know if the Partner track is a fit.",
  },
  {
    n: "02",
    title: "Get approved and get oriented",
    body: "Once approved, you're invited to the Partner Overview Meeting and walked through your enrollment steps.",
  },
  {
    n: "03",
    title: "Start sharing the message",
    body: "Use approved Great Rate Check messaging to create conversations with your network.",
  },
  {
    n: "04",
    title: "Connect consumers to a licensed mortgage pro",
    body: "Interested consumers are routed into the appropriate mortgage review process.",
  },
  {
    n: "05",
    title: "Stay active and supported",
    body: "Receive ongoing content, communication, and program updates.",
  },
];

const FAQS = [
  {
    q: "Do I have to be a licensed Mortgage Loan Originator?",
    a: "No. The Partner path is designed for approved professionals who want to support marketing and business development activity without acting as a licensed loan originator.",
  },
  {
    q: "Can licensed MLOs participate?",
    a: "Yes. Licensed MLOs can use Great Rate Check as part of a broader marketing, referral, and database growth strategy.",
  },
  {
    q: "Can real estate agents participate?",
    a: "Yes. Real estate agents, teams, and brokerages may be a strong fit because they already have client relationships and mortgage-related conversations happening around them.",
  },
  {
    q: "Can a brokerage or team partner at the organization level?",
    a: "Yes. Multiple partnership and joint-venture structures are available for brokerages and team leaders — including brokerage-level revenue opportunities. The right structure depends on your size, licensing posture, and goals, so we cover the specifics on a private strategy call.",
  },
  {
    q: "Will I be quoting rates or giving loan advice?",
    a: "No. Rate quotes, loan structure, loan advice, and mortgage origination activity must be handled by licensed mortgage professionals.",
  },
  {
    q: "What makes Great Rate Check different?",
    a: "Great Rate Check gives partners a simple consumer-facing message, repeatable outreach strategy, monthly content direction, and ongoing support.",
  },
  {
    q: "Can I refer other agents or professionals to the program?",
    a: "Yes. Anyone with a strong network, client database, or interest in mortgage-related business development may be a good fit — send them to the application and we'll review their profile the same way.",
  },
];

/* --- page ----------------------------------------------------- */
export default function BDMProgram() {
  useNoIndex(
    "Partner With Great Rate Check",
    "One conversation engine, three ways to partner — agents & BDMs, licensed MLOs, and brokerages & team leaders."
  );

  // Deep links: /partnerprogram#brokerage etc. auto-scroll to the right track.
  // Path-based pitch links (own OG previews for texted links) land on their track:
  // /partner → BDM track, /mlo → MLO track, /brokerage → brokerage track.
  useEffect(() => {
    const PATH_TRACKS = { "/partner": "bdm", "/mlo": "mlo", "/brokerage": "brokerage", "/program": "program" };
    const scrollToHash = () => {
      let hash = window.location.hash.replace("#", "");
      if (!hash) {
        const path = window.location.pathname.replace(/\.html$/, "").replace(/\/+$/, "") || "/";
        hash = PATH_TRACKS[path] || "";
      }
      if (["bdm", "mlo", "brokerage", "program", "cadence"].includes(hash)) {
        setTimeout(() => {
          document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
        }, 350);
      }
    };
    scrollToHash();
    window.addEventListener("hashchange", scrollToHash);
    return () => window.removeEventListener("hashchange", scrollToHash);
  }, []);

  const goTo = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    window.history.replaceState(null, "", `#${id}`);
  };

  return (
    <div data-testid="bdm-page" className="min-h-screen bg-linen text-navy">
      {/* Minimal top bar */}
      <header className="absolute top-0 inset-x-0 z-40">
        <div className="container-grc flex items-center justify-between h-16 sm:h-20">
          <a href="/" className="inline-flex items-center rounded-2xl bg-white/95 backdrop-blur px-3 py-1.5 shadow-soft" data-testid="bdm-nav-logo">
            <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-9 sm:h-10 w-auto" />
          </a>
          <a
            data-testid="bdm-nav-cta"
            href="/partnerapply"
            className="hidden sm:inline-flex items-center gap-2 bg-lime hover:bg-lime-hover text-navy-deep font-semibold text-sm px-5 py-2.5 rounded-full shadow-cta hover:shadow-cta-hover transition-all hover:-translate-y-0.5"
          >
            Apply to partner <ArrowRight size={16} />
          </a>
        </div>
      </header>

      {/* Hero */}
      <section data-testid="bdm-hero" className="relative overflow-hidden pt-28 sm:pt-32 pb-20 sm:pb-28 grain-bg noise text-white">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
        <div className="container-grc relative max-w-5xl">
          <p className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.22em] uppercase text-lime/90">
            <span className="h-1.5 w-1.5 rounded-full bg-lime animate-pulse" />
            Partner with Great Rate Check
          </p>
          <h1 className="mt-6 font-heading font-medium text-balance text-[2.4rem] leading-[1.06] sm:text-6xl lg:text-[4.25rem] lg:leading-[1.02]">
            One conversation engine. <span className="italic text-lime">Three ways to win.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg sm:text-xl text-white/80 leading-relaxed">
            Great Rate Check turns everyday mortgage-rate conversations into real opportunity — for
            agents and connectors, for licensed originators, and for the brokerages and team leaders
            who want to scale it across their whole roster.
          </p>
          <p className="mt-4 text-white/60 text-sm font-medium uppercase tracking-[0.18em]">
            Choose your track
          </p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="track-chooser">
            {TRACKS.map((t) => (
              <a
                key={t.id}
                href={`#${t.id}`}
                onClick={goTo(t.id)}
                data-testid={`track-card-${t.id}`}
                className="group rounded-3xl border border-white/12 bg-white/[0.06] backdrop-blur-sm p-6 transition-all hover:bg-white/[0.1] hover:-translate-y-1 hover:border-lime/40"
              >
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-lime/15 border border-lime/25 text-lime">
                  {t.icon}
                </span>
                <h3 className="mt-4 font-heading font-medium text-lg leading-snug text-white">{t.who}</h3>
                <p className="mt-2 text-sm text-white/65 leading-relaxed">{t.line}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-lime">
                  {t.cta} <ArrowDown size={14} className="transition-transform group-hover:translate-y-0.5" />
                </span>
              </a>
            ))}
          </div>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-linen/30" />
      </section>

      {/* What is */}
      <section className="section-y bg-linen">
        <div className="container-grc grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="What is Great Rate Check?"
              title="One simple consumer message."
              testid="bdm-what-heading"
            />
          </div>
          <div className="lg:col-span-7 space-y-6 text-lg text-ink-700 leading-relaxed">
            <blockquote className="rounded-3xl border-l-4 border-teal bg-white p-6 sm:p-8 font-heading font-medium text-2xl sm:text-3xl text-navy leading-snug">
              “Before you lock a rate, refinance, or assume you already have the best deal — check it.”
            </blockquote>
            <p>
              Through approved marketing, digital tools, and simple outreach, Partners, real estate professionals, referral
              partners, and MLOs can create mortgage conversations without overcomplicating the process.
            </p>
            <p>
              <strong className="text-navy">The goal:</strong> help more people review their mortgage options while
              helping partners build a scalable business development channel.
            </p>
          </div>
        </div>
      </section>

      {/* ============ TRACK 1 — Agents & BDMs ============ */}
      <TrackHeader
        id="bdm"
        number="1"
        label="For agents, connectors & BDMs"
        title="Turn your sphere into a second revenue stream."
        sub="Become a Great Rate Check Partner — share one simple message, hand mortgage conversations to licensed pros, and earn on every closed loan you help introduce."
        testid="track-header-bdm"
      />

      {/* Who is this for */}
      <section className="section-y bg-white">
        <div className="container-grc">
          <SectionHeading
            eyebrow="Who is this for?"
            title="If you already have relationships, we give you a simple reason to reach out."
            testid="bdm-who-heading"
          />
          <ul className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {AUDIENCE.map((a, i) => (
              <li
                key={i}
                data-testid={`bdm-audience-${i}`}
                className="flex items-center gap-3 rounded-2xl bg-linen border border-linen-border px-5 py-4"
              >
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-teal-light text-teal">
                  {a.icon}
                </span>
                <span className="text-navy font-medium">{a.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Why become a Partner (You do not / You can) */}
      <section className="section-y bg-linen">
        <div className="container-grc">
          <SectionHeading
            eyebrow="Why become a Partner"
            title="Market mortgage services — without originating loans."
            sub="The Partner opportunity is designed for approved professionals who want to help market mortgage services — and earn on every closed loan they help introduce — without being responsible for originating, structuring, processing, underwriting, or closing them."
            testid="bdm-why-heading"
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            <article className="rounded-3xl bg-white border border-linen-border p-8 sm:p-10 shadow-soft" data-testid="bdm-you-do-not">
              <div className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.22em] uppercase text-ink-500">
                <X size={16} /> You do not
              </div>
              <ul className="mt-6 space-y-3">
                {YOU_DO_NOT.map((t, i) => (
                  <li key={i} className="flex items-start gap-3 text-ink-700">
                    <X size={18} className="shrink-0 mt-1 text-ink-400" strokeWidth={2} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </article>
            <article className="rounded-3xl bg-navy text-white p-8 sm:p-10 relative overflow-hidden" data-testid="bdm-you-can">
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-teal/20 blur-3xl" aria-hidden />
              <div className="relative">
                <div className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.22em] uppercase text-lime">
                  <Check size={16} /> You can
                </div>
                <ul className="mt-6 space-y-3">
                  {YOU_CAN.map((t, i) => (
                    <li key={i} className="flex items-start gap-3 text-white/90">
                      <Check size={18} className="shrink-0 mt-1 text-lime" strokeWidth={2.25} />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Why Great Rate Check works */}
      <section className="section-y bg-white">
        <div className="container-grc grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Why it works"
              title="Simple message. Smart conversations."
              testid="bdm-works-heading"
            />
          </div>
          <div className="lg:col-span-7 space-y-6 text-lg text-ink-700 leading-relaxed">
            <p>
              Most people do not know whether the mortgage rate they were offered is truly competitive. Great Rate Check
              gives you a simple call to action:
            </p>
            <blockquote className="rounded-3xl bg-linen border-l-4 border-lime p-6 sm:p-8 font-heading font-medium text-2xl sm:text-3xl text-navy leading-snug">
              “Got a great rate? Let’s check.”
            </blockquote>
            <p>
              It is easy to understand, easy to share, and easy to build a campaign around. It gives Partners, real estate
              agents, MLOs, and referral partners a clean reason to reach out — without sounding pushy or overly
              sales-driven.
            </p>
            <p className="text-navy font-medium">
              No big promises. No complicated pitch. Just smart conversations.
            </p>
          </div>
        </div>
      </section>

      {/* Simple outreach */}
      <section className="section-y bg-linen">
        <div className="container-grc">
          <SectionHeading
            eyebrow="Built for simple outreach"
            title="Create conversations across every mortgage moment."
            testid="bdm-outreach-heading"
          />
          <div className="mt-12 flex flex-wrap gap-3">
            {OUTREACH_TOPICS.map((t, i) => (
              <span
                key={i}
                data-testid={`bdm-topic-${i}`}
                className="inline-flex items-center gap-2 bg-white border border-linen-border rounded-full px-5 py-2.5 text-sm font-medium text-navy shadow-soft"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-teal" /> {t}
              </span>
            ))}
          </div>
          <p className="mt-8 text-ink-600 max-w-2xl">
            You do not need a complicated pitch. You need a simple message people understand.
          </p>
        </div>
      </section>

      {/* CTA band 1 */}
      <CTABand testid="bdm-cta-band-1" />

      {/* For real estate agents */}
      <section className="section-y bg-white">
        <div className="container-grc grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="For real estate agents & teams"
              title="A helpful reason to reconnect."
              sub="Your clients already ask mortgage questions. Your past clients may be wondering if their current loan still makes sense. Your buyers want confidence before they lock a rate."
              testid="bdm-agents-heading"
            />
          </div>
          <ul className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {AGENT_USES.map((t, i) => (
              <li
                key={i}
                data-testid={`bdm-agent-use-${i}`}
                className="rounded-2xl bg-linen border border-linen-border px-5 py-4 flex items-start gap-3"
              >
                <Check size={18} className="shrink-0 mt-0.5 text-teal" strokeWidth={2.25} />
                <span className="text-ink-700">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Monthly content + partner support */}
      <section className="section-y bg-white">
        <div className="container-grc">
          <SectionHeading
            eyebrow="Monthly content + partner support"
            title="More than a link — a full communication system."
            sub="The goal is to keep partners active, supported, and confident in their outreach."
            testid="bdm-support-heading"
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PARTNER_SUPPORT.map((t, i) => (
              <div
                key={i}
                data-testid={`bdm-support-${i}`}
                className="rounded-2xl bg-linen border border-linen-border p-5 flex items-start gap-3 shadow-soft"
              >
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-teal-light text-teal shrink-0">
                  <Wrench size={16} />
                </span>
                <span className="text-navy font-medium">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band 2 */}
      <CTABand testid="bdm-cta-band-2" />

      {/* How it works */}
      <section id="how-it-works" className="section-y bg-white">
        <div className="container-grc">
          <SectionHeading
            eyebrow="How it works"
            title="Five simple steps from curiosity to conversation."
            testid="bdm-how-heading"
          />
          <ol className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {STEPS.map((s, i) => (
              <li
                key={s.n}
                data-testid={`bdm-step-${i + 1}`}
                className="relative rounded-3xl bg-linen border border-linen-border p-6 overflow-hidden"
              >
                <span
                  aria-hidden
                  className="absolute -top-4 -left-2 font-heading font-medium text-[5.5rem] leading-none text-ink-200 select-none pointer-events-none"
                >
                  {s.n}
                </span>
                <div className="relative pt-8">
                  <h3 className="font-heading font-medium text-lg leading-tight">{s.title}</h3>
                  <p className="mt-3 text-sm text-ink-600 leading-relaxed">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* The full program — monthly assets, earnings, NEXA */}
      <FullProgramSection />

      {/* BDM track close — apply row */}
      <section className="section-y bg-linen" data-testid="bdm-track-close">
        <div className="container-grc rounded-3xl bg-white border border-linen-border shadow-soft p-8 sm:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <Eyebrow>Ready for the Partner track?</Eyebrow>
            <h3 className="mt-3 font-heading font-medium text-2xl sm:text-3xl text-navy text-balance max-w-xl">
              The application takes about five minutes.
            </h3>
          </div>
          <ApplyCTA testid="bdm-track-apply" label="Apply to become a Partner" variant="secondary" />
        </div>
      </section>

      {/* ============ TRACK 2 — Licensed MLOs ============ */}
      <TrackHeader
        id="mlo"
        number="2"
        label="For licensed loan originators"
        title="Sharpen your message. Multiply your pipeline."
        sub="Great Rate Check gives licensed MLOs a complete conversation engine — a branded consumer message, a partner network feeding you introductions, and the tools to work your database consistently."
        testid="track-header-mlo"
      />

      <section className="section-y bg-linen">
        <div className="container-grc grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="What you get"
              title="A growth system, not just a link."
              sub="If you are already licensed, Great Rate Check helps you create more conversations and turn your database into consistent activity."
              testid="bdm-mlo-heading"
            />
            <div className="mt-8 space-y-4">
              {[
                { icon: <Home size={18} />, t: "Your own branded landing page", d: "A personal Great Rate Check page with your name, NMLS, and photo — built to share." },
                { icon: <Handshake size={18} />, t: "A partner network working for you", d: "Approved Business Development Partners route their mortgage conversations to you." },
                { icon: <Megaphone size={18} />, t: "Asset library + monthly content", d: "Approved marketing, scripts, and campaign themes — ready to send." },
                { icon: <TrendingUp size={18} />, t: "Dashboard & outreach tools", d: "Track your page, issue assets to partners, and manage your pipeline in one place." },
              ].map((f, i) => (
                <div key={i} data-testid={`mlo-feature-${i}`} className="flex items-start gap-4 rounded-2xl bg-white border border-linen-border p-5 shadow-soft">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-teal-light text-teal shrink-0">{f.icon}</span>
                  <div>
                    <div className="font-heading font-medium text-navy">{f.t}</div>
                    <p className="mt-1 text-sm text-ink-600 leading-relaxed">{f.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {MLO_SUPPORT.map((t, i) => (
                <li
                  key={i}
                  data-testid={`bdm-mlo-support-${i}`}
                  className="rounded-2xl bg-white border border-linen-border px-5 py-4 flex items-start gap-3"
                >
                  <Check size={18} className="shrink-0 mt-0.5 text-teal" strokeWidth={2.25} />
                  <span className="text-ink-700">{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                data-testid="mlo-track-apply"
                href="/mlogrowth"
                className="inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white font-semibold text-base px-8 py-4 rounded-full shadow-cta hover:shadow-cta-hover transition-all hover:-translate-y-0.5"
              >
                Apply to the MLO Growth Program <ArrowRight size={18} />
              </a>
              <a
                data-testid="mlo-track-learn"
                href="/mloprogram"
                className="inline-flex items-center justify-center gap-2 bg-white border border-linen-border hover:border-teal text-navy font-semibold text-base px-8 py-4 rounded-full transition-all hover:-translate-y-0.5"
              >
                Explore the MLO program <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TRACK 3 — Brokerages & Team Leaders ============ */}
      <TrackHeader
        id="brokerage"
        number="3"
        label="For brokerages & team leaders"
        title="Recruit. Retain. Reward. At brokerage scale."
        sub="Put a branded conversation engine in the hands of every agent on your roster — and open brokerage-level revenue opportunities on top of it."
        testid="track-header-brokerage"
      />

      <section className="section-y bg-white" data-testid="brokerage-section">
        <div className="container-grc grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-6 space-y-8">
            <div className="grid grid-cols-1 gap-4">
              {[
                {
                  icon: <Users size={18} />,
                  t: "Recruit",
                  d: "Offer prospective agents something most brokerages can't: a done-for-you conversation system with real earning potential attached to the relationships they already have.",
                },
                {
                  icon: <Sparkles size={18} />,
                  t: "Retain",
                  d: "Give your agents a fresh, compliant reason to touch their sphere every month — approved messaging, monthly themes, and campaigns that keep them producing (and staying).",
                },
                {
                  icon: <Award size={18} />,
                  t: "Reward",
                  d: "Approved agents earn on every closed loan they help introduce — a revenue stream layered on top of their commissions, powered by conversations they're already having.",
                },
              ].map((r, i) => (
                <div key={i} data-testid={`brokerage-r-${i}`} className="rounded-3xl bg-linen border border-linen-border p-6 sm:p-7 flex items-start gap-4">
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-navy text-lime shrink-0">{r.icon}</span>
                  <div>
                    <h3 className="font-heading font-medium text-xl text-navy">{r.t}</h3>
                    <p className="mt-1.5 text-ink-600 leading-relaxed">{r.d}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="rounded-3xl bg-navy text-white p-7 sm:p-8 relative overflow-hidden" data-testid="brokerage-jv-card">
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-teal/25 blur-3xl" aria-hidden />
              <div className="relative">
                <p className="text-xs font-bold tracking-[0.22em] uppercase text-lime">Beyond agent earnings</p>
                <h3 className="mt-3 font-heading font-medium text-2xl leading-snug">
                  Multiple partnership and joint-venture structures available — including brokerage-level revenue opportunities.
                </h3>
                <p className="mt-3 text-white/70 leading-relaxed">
                  The right structure depends on your size, licensing posture, and goals. We walk through
                  the options together on a private strategy call.
                </p>
                <a
                  data-testid="brokerage-onepager-download"
                  href={`${API}/brokerage-onepager.pdf`}
                  onClick={() => track("brokerage_onepager_download", { placement: "jv_card" })}
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/25 text-white font-semibold text-sm px-5 py-2.5 transition-colors"
                >
                  <Download size={15} className="text-lime" /> Download the one-pager (PDF)
                </a>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6 lg:sticky lg:top-8">
            <BrokerageInquiryForm />
          </div>
        </div>
      </section>

      {/* Brokerage operating cadence — deep link target: /brokerage#cadence */}
      <BrokerageCadence />

      {/* After you're approved */}
      <section className="section-y bg-linen">
        <div className="container-grc">
          <div
            data-testid="bdm-after-approval"
            className="rounded-3xl bg-white border border-linen-border p-8 sm:p-12 shadow-soft grid grid-cols-1 lg:grid-cols-12 gap-10 items-center"
          >
            <div className="lg:col-span-7">
              <Eyebrow>After you're approved</Eyebrow>
              <h2 className="mt-4 font-heading font-medium text-3xl sm:text-4xl text-navy text-balance">
                Approved partners are invited to the Partner Overview Meeting and walked through enrollment.
              </h2>
              <p className="mt-5 text-ink-600 leading-relaxed max-w-2xl">
                There's nothing to book today. Apply first — if your profile is a fit, your approval message
                carries the meeting invitation and your enrollment steps.
              </p>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <li className="flex items-center gap-2 text-ink-700" data-testid="bdm-meeting-length">
                  <CalendarClock size={18} className="text-teal" /> 45 minutes
                </li>
                <li className="flex items-center gap-2 text-ink-700" data-testid="bdm-meeting-format">
                  <MessageSquare size={18} className="text-teal" /> Online overview
                </li>
                <li className="flex items-center gap-2 text-ink-700" data-testid="bdm-meeting-best-for">
                  <Users size={18} className="text-teal" /> Invitation after approval
                </li>
              </ul>
            </div>
            <div className="lg:col-span-5 flex lg:justify-end">
              <ApplyCTA testid="bdm-after-approval-cta" label="Apply to become a Partner" variant="secondary" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-y bg-white">
        <div className="container-grc max-w-4xl">
          <div className="text-center">
            <Eyebrow>Frequently asked</Eyebrow>
            <h2 className="mt-4 font-heading font-medium text-3xl sm:text-4xl text-balance">
              Common questions about the program.
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-10">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`bdm-faq-${i}`} data-testid={`bdm-faq-item-${i}`} className="border-b border-linen-border">
                <AccordionTrigger
                  data-testid={`bdm-faq-trigger-${i}`}
                  className="text-left font-heading font-medium text-lg sm:text-xl text-navy hover:no-underline hover:text-teal transition-colors py-5"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent
                  data-testid={`bdm-faq-content-${i}`}
                  className="text-base leading-relaxed text-ink-600 pb-6"
                >
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section data-testid="bdm-final-cta" className="relative section-y overflow-hidden grain-bg noise text-white">
        <div className="container-grc relative text-center max-w-3xl">
          <Eyebrow>Start with one simple question</Eyebrow>
          <h2 className="mt-5 font-heading font-medium text-4xl sm:text-6xl leading-[1.05] text-balance">
            “Got a great rate? <span className="italic text-lime">Let’s check.”</span>
          </h2>
          <p className="mt-6 text-lg text-white/75">
            That one question can open the door to better conversations, stronger client service, and new business
            opportunities.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
            <ApplyCTA testid="bdm-final-apply" label="Apply to become a Partner" variant="primary" />
            <a
              href={CONTACT.phoneHref}
              data-testid="bdm-final-cta-call"
              className="text-white/90 hover:text-white font-medium underline decoration-white/30 underline-offset-4 hover:decoration-lime transition-colors inline-flex items-center gap-2"
            >
              <Phone size={16} /> Or call {CONTACT.phoneDisplay}
            </a>
          </div>
        </div>
      </section>

      {/* Minimal footer */}
      <footer className="bg-navy-deep text-white/70 py-10">
        <div className="container-grc flex flex-col sm:flex-row items-center justify-between gap-4 text-xs">
          <div className="inline-flex items-center rounded-xl bg-white/95 px-3 py-1.5">
            <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-7 w-auto" />
          </div>
          <p className="text-white/50">© {new Date().getFullYear()} Great Rate Check · Private partner overview</p>
        </div>
      </footer>
    </div>
  );
}

function CTABand({ testid }) {
  return (
    <section data-testid={testid} className="relative py-14 sm:py-20 bg-navy text-white overflow-hidden">
      <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-teal/25 blur-3xl" aria-hidden />
      <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-lime/15 blur-3xl" aria-hidden />
      <div className="container-grc relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div>
          <Eyebrow>Start here</Eyebrow>
          <h3 className="mt-3 font-heading font-medium text-2xl sm:text-3xl text-balance max-w-2xl">
            Apply first — about five minutes. Once you're approved, we walk you through the rest.
          </h3>
        </div>
        <ApplyCTA testid={`${testid}-cta`} label="Apply to become a Partner" variant="lime" />
      </div>
    </section>
  );
}

/* --- Brokerage inquiry form ----------------------------------- */
const AGENT_COUNT_OPTIONS = ["1–10", "11–50", "51–100", "101–300", "300+"];

function BrokerageInquiryForm() {
  const [f, setF] = useState({
    full_name: "",
    brokerage_name: "",
    agent_count: "",
    email: "",
    phone: "",
    city_state: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const canSubmit = f.full_name.trim() && f.brokerage_name.trim() && f.agent_count && f.email.trim() && f.phone.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || sending) return;
    setSending(true);
    setErr("");
    try {
      await axios.post(`${API}/brokerage-inquiries`, f);
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-3xl bg-navy text-white p-8 sm:p-10 text-center shadow-cta" data-testid="brokerage-form-success">
        <span className="mx-auto inline-flex items-center justify-center w-14 h-14 rounded-full bg-lime/20">
          <CheckCircle2 size={28} className="text-lime" />
        </span>
        <h3 className="mt-5 font-heading font-medium text-2xl">Inquiry received.</h3>
        <p className="mt-3 text-white/75 leading-relaxed">
          Thanks — we'll reach out shortly to schedule your private strategy call and walk through the
          partnership structures available for your organization.
        </p>
        <a
          data-testid="brokerage-success-onepager"
          href={`${API}/brokerage-onepager.pdf`}
          onClick={() => track("brokerage_onepager_download", { placement: "form_success" })}
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-lime hover:bg-lime-hover text-navy-deep font-semibold text-sm px-6 py-3 transition-colors"
        >
          <Download size={15} /> Grab the one-pager while you wait
        </a>
      </div>
    );
  }

  const inputCls =
    "w-full rounded-xl border border-linen-border bg-white focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20 p-3 text-sm text-navy";

  return (
    <form
      onSubmit={submit}
      data-testid="brokerage-inquiry-form"
      className="rounded-3xl bg-linen border border-linen-border p-7 sm:p-8 shadow-soft"
    >
      <p className="text-xs font-bold tracking-[0.22em] uppercase text-teal">Brokerage & team leaders</p>
      <h3 className="mt-3 font-heading font-medium text-2xl text-navy">Book a private strategy call.</h3>
      <p className="mt-2 text-sm text-ink-600 leading-relaxed">
        Tell us about your organization and we'll reach out to walk through the partnership options.
      </p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block sm:col-span-1">
          <span className="text-sm font-medium text-navy">Your name *</span>
          <input data-testid="brokerage-name-input" value={f.full_name} onChange={set("full_name")} required className={`mt-1.5 ${inputCls}`} />
        </label>
        <label className="block sm:col-span-1">
          <span className="text-sm font-medium text-navy">Brokerage / team *</span>
          <input data-testid="brokerage-company-input" value={f.brokerage_name} onChange={set("brokerage_name")} required className={`mt-1.5 ${inputCls}`} />
        </label>
        <label className="block sm:col-span-1">
          <span className="text-sm font-medium text-navy">Number of agents *</span>
          <select data-testid="brokerage-agents-select" value={f.agent_count} onChange={set("agent_count")} required className={`mt-1.5 ${inputCls}`}>
            <option value="">Select…</option>
            {AGENT_COUNT_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </label>
        <label className="block sm:col-span-1">
          <span className="text-sm font-medium text-navy">City / state</span>
          <input data-testid="brokerage-city-input" value={f.city_state} onChange={set("city_state")} className={`mt-1.5 ${inputCls}`} />
        </label>
        <label className="block sm:col-span-1">
          <span className="text-sm font-medium text-navy">Email *</span>
          <input data-testid="brokerage-email-input" type="email" value={f.email} onChange={set("email")} required className={`mt-1.5 ${inputCls}`} />
        </label>
        <label className="block sm:col-span-1">
          <span className="text-sm font-medium text-navy">Phone *</span>
          <input data-testid="brokerage-phone-input" value={f.phone} onChange={set("phone")} required className={`mt-1.5 ${inputCls}`} />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-sm font-medium text-navy">Anything we should know?</span>
          <textarea data-testid="brokerage-message-input" value={f.message} onChange={set("message")} rows={3} className={`mt-1.5 ${inputCls}`} placeholder="Goals, timing, current lending relationships…" />
        </label>
      </div>
      {err && (
        <p className="mt-4 text-sm text-red-600" data-testid="brokerage-form-error">{err}</p>
      )}
      <button
        type="submit"
        disabled={!canSubmit || sending}
        data-testid="brokerage-form-submit"
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light disabled:opacity-50 text-white font-semibold px-8 py-4 rounded-full shadow-cta transition-all hover:-translate-y-0.5"
      >
        {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={17} />}
        Request my strategy call
      </button>
      <p className="mt-3 text-[11px] text-ink-500 text-center">
        Goes straight to our founding team. No spam, no obligation.
      </p>
    </form>
  );
}

/* --- The Full Program: monthly assets showcase + earnings + NEXA ---------- */
const ASSET_CATEGORIES = [
  {
    key: "texts",
    icon: MessageCircle,
    label: "Texts",
    line: "Ready-to-send messages for past clients, new buyers, and rate-curious contacts.",
  },
  {
    key: "scripts",
    icon: ScrollText,
    label: "Scripts",
    line: "What to say at an open house, on a call, or when rates come up naturally.",
  },
  {
    key: "collateral",
    icon: ImageIcon,
    label: "Digital collateral",
    line: "Branded social graphics, story templates, and printable QR cards.",
  },
  {
    key: "emails",
    icon: Mail,
    label: "Emails",
    line: "Monthly-themed outreach emails — written, branded, and compliance-checked.",
  },
];

function AssetPreview({ cat, featured }) {
  if (cat === "collateral" && featured?.length > 0) {
    return (
      <div data-testid="program-featured-thumbs">
        <div className="text-[10px] uppercase tracking-[0.2em] text-teal font-bold mb-3">
          Straight from this month's library
        </div>
        <div className="flex items-stretch justify-center gap-4 flex-wrap">
          {featured.slice(0, 3).map((a) => (
            <figure key={a.id} className="w-40 sm:w-44">
              <div className="rounded-xl overflow-hidden border border-linen-border shadow-cta bg-white aspect-[4/5]">
                <img
                  src={`${API}/program/featured-assets/${a.id}/thumb`}
                  alt={a.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <figcaption className="mt-2 text-[11px] text-ink-500 text-center leading-snug truncate">{a.title}</figcaption>
            </figure>
          ))}
        </div>
      </div>
    );
  }
  if (cat === "texts") {
    return (
      <div className="space-y-3">
        <div className="max-w-[85%] rounded-2xl rounded-bl-md bg-white border border-linen-border px-4 py-3 text-sm text-navy shadow-soft">
          Hey Sarah! Quick thought — you closed on your place a couple years back. Worth a 2-minute
          rate checkup? No credit pull, just a straight answer. 🏡
        </div>
        <div className="max-w-[85%] ml-auto rounded-2xl rounded-br-md bg-[#1F8F8B] px-4 py-3 text-sm text-white shadow-soft">
          Ooh yes — I've been wondering about that actually!
        </div>
        <div className="max-w-[85%] rounded-2xl rounded-bl-md bg-white border border-linen-border px-4 py-3 text-sm text-navy shadow-soft">
          Easy — here's the link. Takes about two minutes: <span className="text-teal font-medium">greatratecheck.com/jason</span>
        </div>
      </div>
    );
  }
  if (cat === "scripts") {
    return (
      <div className="rounded-2xl bg-white border border-linen-border p-5 shadow-soft">
        <div className="text-[10px] uppercase tracking-[0.2em] text-teal font-bold">Open house script</div>
        <p className="mt-3 text-sm text-navy leading-relaxed italic">
          “Before you go — grab one of those cards by the sign-in sheet. It's a free two-minute rate
          checkup. Whether you're buying or you already own, it just tells you if the rate you've got
          is still the best one out there. No credit pull, no strings.”
        </p>
        <div className="mt-4 flex items-center gap-2 text-[11px] text-ink-500">
          <Check size={12} className="text-teal" /> Compliance-reviewed
          <Check size={12} className="text-teal ml-2" /> Updated monthly
        </div>
      </div>
    );
  }
  if (cat === "collateral") {
    return (
      <div className="flex items-center justify-center gap-4">
        <div className="w-44 rounded-xl overflow-hidden shadow-cta border border-linen-border bg-navy text-center pb-4">
          <div className="pt-4 pb-3 px-3">
            <div className="text-[8px] uppercase tracking-[0.25em] text-lime font-bold">Great Rate Check</div>
            <div className="mt-1.5 text-white font-heading font-medium text-sm leading-snug">Got a great rate?</div>
            <div className="text-lime font-heading font-medium text-sm">Let's check.</div>
          </div>
          <div className="mx-auto w-16 h-16 bg-white rounded-lg p-1.5">
            <div className="w-full h-full rounded-sm" style={{ backgroundImage: "repeating-linear-gradient(0deg,#0F2A47 0 3px,transparent 3px 6px), repeating-linear-gradient(90deg,#0F2A47 0 3px,transparent 3px 6px)" }} />
          </div>
          <div className="mt-2 text-[8px] text-white/70">Scan to check your rate</div>
        </div>
        <div className="hidden sm:block w-32 rounded-xl bg-white border border-linen-border shadow-soft p-3">
          <div className="h-16 rounded-lg bg-gradient-to-br from-[#0F2A47] to-[#1F8F8B]" />
          <div className="mt-2 h-2 rounded bg-linen" />
          <div className="mt-1 h-2 w-3/4 rounded bg-linen" />
          <div className="mt-2 text-[8px] text-ink-400">Story template · 9:16</div>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl bg-white border border-linen-border shadow-soft overflow-hidden">
      <div className="px-5 py-3 bg-linen border-b border-linen-border flex items-center gap-2">
        <Mail size={13} className="text-teal" />
        <span className="text-xs text-ink-500">Monthly outreach email · September theme</span>
      </div>
      <div className="p-5">
        <div className="text-sm font-semibold text-navy">Subject: Your mortgage deserves an annual checkup too</div>
        <p className="mt-2 text-[13px] text-ink-600 leading-relaxed">
          Hi Sarah — you get your car serviced and your teeth cleaned every year. When's the last time
          anyone checked your mortgage rate? It takes about two minutes, there's no credit pull, and
          you'll know exactly where you stand…
        </p>
        <div className="mt-3 inline-flex rounded-full bg-lime px-4 py-1.5 text-[11px] font-bold text-navy-deep">Check my rate →</div>
      </div>
    </div>
  );
}

/* --- Brokerage operating cadence (deep link: /brokerage#cadence) ----------- */
const BROKERAGE_CADENCE = [
  {
    cadence: "Weekly",
    icon: PlayCircle,
    title: "The Field Update",
    line: "Every approved agent on your roster gets a 3-minute brief: the message of the week, one copy-paste outreach script, and one action item. Nobody has to invent content or wonder what to post.",
  },
  {
    cadence: "Monthly",
    icon: Video,
    title: "Partner Growth Meeting",
    line: "A live 45-minute meeting on a fixed agenda: program update, compliance and messaging alignment, the month's marketing theme, one growth tactic, then wins and the Top Partner of the Month.",
  },
  {
    cadence: "Quarterly",
    icon: Compass,
    title: "Leadership Strategy Review",
    line: "A session for you, not your agents: what's converting across the roster, where agents stalled, what we change next quarter, and how the partnership structure is performing.",
  },
  {
    cadence: "As needed",
    icon: ShieldCheck,
    title: "Compliance updates",
    line: "When approved language changes, it goes to every partner the same day — and the old version comes down. Your agents are never left improvising around rate talk.",
  },
];

const BROKERAGE_OUTCOMES = [
  {
    icon: TrendingUp,
    t: "Agent activation, not just onboarding",
    d: "Each approved agent gets a 30-day activation journey — one next step at a time, with streaks for the weeks they hit. We don't hand out a login and hope. Agents who stall get nudged before they go quiet.",
  },
  {
    icon: Users,
    t: "Roster-level visibility",
    d: "You see who's active, who's stalled, and who just logged a first win — so your coaching goes to the agents who actually need it instead of the whole room.",
  },
  {
    icon: ShieldCheck,
    t: "Compliance managed for you",
    d: "Approved messaging only, RESPA-conscious guardrails, a clean handoff to licensed originators, and partner activity logged. Your agents never give mortgage advice, and you never police their captions.",
  },
];

function BrokerageCadence() {
  return (
    <section
      id="cadence"
      data-testid="brokerage-cadence"
      className="section-y bg-linen scroll-mt-16"
    >
      <div className="container-grc">
        <div className="max-w-3xl">
          <Eyebrow>The brokerage operating cadence</Eyebrow>
          <h2 className="mt-4 font-heading font-medium text-3xl sm:text-4xl lg:text-5xl text-balance text-navy">
            What your roster gets from us, <span className="italic text-teal">every single month.</span>
          </h2>
          <p className="mt-5 text-lg text-ink-600 leading-relaxed">
            Day-one excitement is easy — month-six momentum is engineered. This is the communication,
            compliance, and activation rhythm we run on your behalf, so your agents always know what to
            say and you never become the content department.
          </p>
        </div>

        <div className="mt-12 rounded-3xl bg-navy text-white p-7 sm:p-10 relative overflow-hidden">
          <div className="absolute -top-20 -left-20 w-64 h-64 rounded-full bg-teal/20 blur-3xl" aria-hidden />
          <div className="absolute -bottom-24 -right-16 w-72 h-72 rounded-full bg-lime/10 blur-3xl" aria-hidden />
          <div className="relative grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {BROKERAGE_CADENCE.map(({ cadence, icon: Icon, title, line }, i) => (
              <motion.div
                key={cadence}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: i * 0.1 }}
                className="rounded-2xl bg-white/[0.06] border border-white/10 p-5 sm:p-6 backdrop-blur-sm"
                data-testid={`brokerage-cadence-${cadence.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-lime text-navy-deep">
                    <Icon size={17} />
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-lime">{cadence}</span>
                </div>
                <div className="mt-4 font-heading font-medium text-lg text-white">{title}</div>
                <p className="mt-2 text-[13px] text-white/70 leading-relaxed">{line}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {BROKERAGE_OUTCOMES.map(({ icon: Icon, t, d }, i) => (
            <motion.div
              key={t}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="rounded-3xl bg-white border border-linen-border shadow-soft p-7 sm:p-8"
              data-testid={`brokerage-outcome-${i}`}
            >
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-teal-light text-teal">
                <Icon size={20} />
              </span>
              <h3 className="mt-4 font-heading font-medium text-xl leading-snug text-navy">{t}</h3>
              <p className="mt-3 text-ink-600 leading-relaxed">{d}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-8 rounded-3xl bg-white border border-linen-border shadow-soft p-7 sm:p-9 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <Eyebrow>Want it walked through?</Eyebrow>
            <h3 className="mt-3 font-heading font-medium text-2xl sm:text-3xl text-navy text-balance max-w-xl">
              We'll map the cadence to your roster size on a private strategy call.
            </h3>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="#brokerage"
              data-testid="brokerage-cadence-strategy-cta"
              className="inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white font-semibold text-base px-7 py-3.5 rounded-full shadow-cta hover:shadow-cta-hover transition-all hover:-translate-y-0.5"
            >
              Request a strategy call <ArrowRight size={17} />
            </a>
            <a
              href={`${API}/brokerage-onepager.pdf`}
              onClick={() => track("brokerage_onepager_download", { placement: "cadence" })}
              data-testid="brokerage-cadence-onepager"
              className="inline-flex items-center justify-center gap-2 bg-linen hover:bg-[#EDE9DC] border border-linen-border text-navy font-semibold text-base px-7 py-3.5 rounded-full transition-colors"
            >
              <Download size={16} className="text-teal" /> One-pager (PDF)
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --- Operating rhythm: weekly / monthly / quarterly cadence ---------------- */
const RHYTHM = [
  {
    cadence: "Weekly",
    icon: PlayCircle,
    title: "The Field Update",
    line: "A 3-minute brief with a short video from the Great Rate Check team and network MLOs — the message of the week, one copy-paste outreach script, one confidence check, and one clear action item.",
  },
  {
    cadence: "Monthly",
    icon: Video,
    title: "Partner Growth Meeting",
    line: "A live, well-produced 45-minute virtual meeting: program updates, the marketing theme of the month, one practical growth tactic, and real wins from the field — including the Top Partner of the Month.",
  },
  {
    cadence: "Quarterly",
    icon: Compass,
    title: "Strategy Review",
    line: "A leadership-level look at what's converting, what partners need next, and where the program goes from here.",
  },
];

function OperatingRhythm() {
  return (
    <div className="mt-8" data-testid="program-rhythm">
      <div className="rounded-3xl bg-navy text-white p-7 sm:p-10 relative overflow-hidden">
        <div className="absolute -top-20 -left-20 w-64 h-64 rounded-full bg-teal/20 blur-3xl" aria-hidden />
        <div className="absolute -bottom-24 -right-16 w-72 h-72 rounded-full bg-lime/10 blur-3xl" aria-hidden />
        <div className="relative">
          <div className="text-[11px] uppercase tracking-[0.2em] text-lime font-bold">The operating rhythm</div>
          <h3 className="mt-3 font-heading font-medium text-2xl sm:text-3xl leading-snug text-balance max-w-2xl" data-testid="program-rhythm-heading">
            Most partner programs hand you a login and disappear. We built an operating rhythm.
          </h3>
          <p className="mt-3 text-white/70 leading-relaxed max-w-2xl">
            Day-one excitement is easy. Month-six momentum is engineered. The program runs on a steady
            cadence of communication, coaching, and recognition — so you always know what to say, what to
            do next, and that someone's paying attention.
          </p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {RHYTHM.map(({ cadence, icon: Icon, title, line }, i) => (
              <motion.div
                key={cadence}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: i * 0.12 }}
                className="relative rounded-2xl bg-white/[0.06] border border-white/10 p-5 sm:p-6 backdrop-blur-sm"
                data-testid={`program-rhythm-${cadence.toLowerCase()}`}
              >
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-lime text-navy-deep">
                    <Icon size={17} />
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-lime">{cadence}</span>
                </div>
                <div className="mt-4 font-heading font-medium text-lg text-white">{title}</div>
                <p className="mt-2 text-[13px] text-white/70 leading-relaxed">{line}</p>
                {i < 2 && (
                  <ArrowRight size={15} className="hidden md:block absolute -right-2.5 top-1/2 -translate-y-1/2 text-white/25" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Confidence + recognition band */}
      <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5 }}
          className="rounded-3xl bg-white border border-linen-border shadow-soft p-7 sm:p-8"
          data-testid="program-confidence-card"
        >
          <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-teal-light text-teal">
            <MessageSquare size={20} />
          </span>
          <h3 className="mt-4 font-heading font-medium text-2xl leading-snug text-navy">
            Confidence, not guesswork.
          </h3>
          <p className="mt-3 text-ink-600 leading-relaxed">
            You'll always know exactly what to say. Every month brings approved language, real
            do-and-don't examples, and a clean handoff to licensed pros — so rate conversations feel
            natural, stay clean, and never put you on the spot.
          </p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, delay: 0.12 }}
          className="rounded-3xl bg-white border border-linen-border shadow-soft p-7 sm:p-8"
          data-testid="program-recognition-card"
        >
          <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-lime text-navy-deep">
            <Trophy size={20} />
          </span>
          <h3 className="mt-4 font-heading font-medium text-2xl leading-snug text-navy">
            Recognition that means something.
          </h3>
          <p className="mt-3 text-ink-600 leading-relaxed">
            The ten agreements you sign aren't fine print — they're the operating system. We celebrate
            first wins, spotlight the Top Partner of the Month, and recognize the consistent activity
            that actually builds a pipeline — not just the biggest numbers.
          </p>
        </motion.div>
      </div>
    </div>
  );
}

function FullProgramSection() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/program/featured-assets`)
      .then((r) => setFeatured((r.data || []).filter((a) => (a.content_type || "").startsWith("image/"))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setActive((a) => (a + 1) % ASSET_CATEGORIES.length), 4500);
    return () => clearInterval(t);
  }, [paused]);

  return (
    <section id="program" className="section-y bg-linen scroll-mt-16" data-testid="full-program-section">
      <div className="container-grc">
        <SectionHeading
          eyebrow="The full program"
          title="You bring relationships. We hand you the machine."
          sub="This isn't a link and a pat on the back. Every month, approved partners receive a fresh round of done-for-you marketing — written, designed, and compliance-checked — so staying in front of your sphere never depends on you creating content."
          testid="full-program-heading"
        />

        {/* Monthly assets: category rail + live preview */}
        <div
          className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className="lg:col-span-5 space-y-3" data-testid="program-asset-rail">
            <div className="text-[11px] uppercase tracking-[0.2em] text-teal font-bold mb-1">
              What lands in your hands every month
            </div>
            {ASSET_CATEGORIES.map((c, i) => {
              const Icon = c.icon;
              const isActive = i === active;
              return (
                <button
                  key={c.key}
                  onClick={() => setActive(i)}
                  data-testid={`program-asset-${c.key}`}
                  className={`w-full text-left rounded-2xl border p-4 transition-all duration-300 ${
                    isActive
                      ? "bg-navy border-navy shadow-cta -translate-y-0.5"
                      : "bg-white border-linen-border hover:border-teal"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span className={`inline-flex items-center justify-center w-9 h-9 rounded-xl shrink-0 transition-colors ${isActive ? "bg-lime text-navy-deep" : "bg-teal-light text-teal"}`}>
                      <Icon size={16} />
                    </span>
                    <div>
                      <div className={`font-heading font-medium ${isActive ? "text-white" : "text-navy"}`}>{c.label}</div>
                      <p className={`mt-0.5 text-[13px] leading-snug ${isActive ? "text-white/70" : "text-ink-500"}`}>{c.line}</p>
                    </div>
                  </div>
                  {isActive && !paused && (
                    <motion.div
                      key={`bar-${active}`}
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ duration: 4.5, ease: "linear" }}
                      className="mt-3 h-0.5 rounded-full bg-lime origin-left"
                    />
                  )}
                </button>
              );
            })}
          </div>
          <div className="lg:col-span-7 rounded-3xl bg-[#EDE9DC]/60 border border-linen-border p-6 sm:p-8 flex items-center" data-testid="program-asset-preview">
            <AnimatePresence mode="wait">
              <motion.div
                key={ASSET_CATEGORIES[active].key}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35 }}
                className="w-full"
              >
                <AssetPreview cat={ASSET_CATEGORIES[active].key} featured={featured} />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Operating rhythm — cadence + confidence + recognition */}
        <OperatingRhythm />

        {/* Earnings + NEXA band */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }}
            className="rounded-3xl bg-navy text-white p-7 sm:p-8 relative overflow-hidden"
            data-testid="program-earnings-card"
          >
            <div className="absolute -top-14 -right-14 w-48 h-48 rounded-full bg-lime/15 blur-3xl" aria-hidden />
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-lime text-navy-deep">
              <BadgeDollarSign size={20} />
            </span>
            <h3 className="mt-4 font-heading font-medium text-2xl leading-snug">
              This is a paid program — not a favor.
            </h3>
            <p className="mt-3 text-white/75 leading-relaxed">
              Approved partners <strong className="text-lime">earn on every closed loan they help introduce</strong>.
              Real earnings, real structure, fully compliant — we walk through exactly how it works
              during your application review.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.12 }}
            className="rounded-3xl bg-white border border-linen-border shadow-soft p-7 sm:p-8"
            data-testid="program-nexa-card"
          >
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-teal-light text-teal">
              <ShieldCheck size={20} />
            </span>
            <h3 className="mt-4 font-heading font-medium text-2xl leading-snug text-navy">
              Backed by NEXA Mortgage.
            </h3>
            <p className="mt-3 text-ink-600 leading-relaxed">
              Your people aren't handed to a call center. They work with seasoned loan originators at
              one of the nation's largest mortgage brokerages — with access to competitive rates from a
              broad marketplace of lenders and service that reflects well on <em>you</em>.
            </p>
          </motion.div>
        </div>

        {/* Animated flow strip */}
        <div className="mt-8 rounded-3xl bg-white border border-linen-border shadow-soft p-6 sm:p-8" data-testid="program-flow">
          <div className="text-[11px] uppercase tracking-[0.2em] text-teal font-bold">The whole motion, start to finish</div>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-4 gap-5 sm:gap-3">
            {[
              { icon: MessageCircle, t: "Talk", d: "Use the monthly assets to spark rate conversations." },
              { icon: Handshake, t: "Hand off", d: "Share one link — a simple, compliant handoff." },
              { icon: ShieldCheck, t: "We handle it", d: "NEXA-backed loan pros take the mortgage side." },
              { icon: BadgeDollarSign, t: "You earn", d: "Approved partners earn on closed loans they introduce." },
            ].map(({ icon: Icon, t, d }, i) => (
              <motion.div
                key={t}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: i * 0.15 }}
                className="relative sm:pr-4"
                data-testid={`program-flow-step-${i}`}
              >
                <div className="flex items-center gap-2.5">
                  <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full ${i === 3 ? "bg-lime text-navy-deep" : "bg-navy text-lime"}`}>
                    <Icon size={15} />
                  </span>
                  <span className="font-heading font-medium text-navy">{t}</span>
                </div>
                <p className="mt-2 text-[13px] text-ink-500 leading-snug">{d}</p>
                {i < 3 && (
                  <ArrowRight size={15} className="hidden sm:block absolute -right-1 top-2.5 text-linen-border" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
